import React from "react";
import { css } from "@emotion/react";
import WizardLink from "./WizardLink";
import { ReactComponent as ArrowFilled } from "../../images/icons/arrow_filled.svg";
import theme from "../../theme";

type Props = {
    progress: string,
    stageNumber: number,
    stageText: string,
    link?: string,
    totalLength: number,
    isCurrent: boolean,
};

const WizardOption: React.FC<Props> = ({
   progress,
   stageNumber,
   stageText,
   link,
   totalLength,
   isCurrent
}) => (
    <div css={wizardStageStyle(progress, stageNumber === 1, totalLength, stageNumber, !!link)}>
        <WizardLink link={link} css={linkStyle}>
            <div css={stageInfoStyle}>
                <div css={stageNumberContainerStyle(progress)}>
                    <p css={stageNumberStyle(progress)}>
                        {stageNumber}
                    </p>
                </div>
                <div css={stageTextStyle(progress)}>
                    {stageText}
                </div>
            </div>
            {isCurrent && <ArrowFilled css={arrowStyle} />}
        </WizardLink>
    </div>
);

const wizardStageStyle = (progress: string, isFirstStage: boolean, totalLength: number, stageNumber: number, hasLink: boolean) => css`
    display: flex;
    justify-content: space-between;
    width: ${(100 / totalLength).toFixed(2)}%;
    height: 100%;
    position: relative;
    z-index: ${totalLength};
    flex-grow: 1;
    border: 2px solid ${progress === 'todo' ? theme.colours.curiousBlue : theme.colours.white};
    ${isFirstStage ? css`
        border-radius: 16px;
    ` : css`
        z-index: ${totalLength - stageNumber};
        margin-left: -13px;
        width: calc(${(100 / totalLength).toFixed(2)}%);
        border-top-right-radius: ${theme.borderAndShadow.largerRadius1};
        border-bottom-right-radius: ${theme.borderAndShadow.largerRadius1};
        
        ${theme.breakpoints.down("md")} {
            justify-content: center;
        }
    `};

    background-color: ${progress === 'todo' ? theme.colours.white : theme.colours.curiousBlue};

    ${progress !== 'todo' && css`
        box-shadow: 5px 0 4px #00000029;
    `};

    ${hasLink && css`
        &:hover {
            cursor: pointer;
            background-color: ${theme.colours.lochmara3};

            &:after {
                background-color: ${theme.colours.lochmara3};
                border-left: 1px solid ${theme.colours.lochmara3};
                border-bottom: 1px solid ${theme.colours.lochmara3};
            }
        }
    `};
    &:first-of-type {
        > a > div {
            margin-left: 11px;
            
            ${theme.breakpoints.down("xs")} {
                margin-left: 0;
            }
        }
    }
`;

const linkStyle = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    ${theme.breakpoints.down("md")} {
        justify-content: center;
    }
`;

const stageInfoStyle = css`
    display: flex;
    margin-left: 25px;
    color: white;
    
    ${theme.breakpoints.down("md")} {
        margin-left: 0;
    }
`;

const stageNumberContainerStyle = (progress: string) => css`
    display: flex;
    text-align: center;
    margin: auto 0;
    height: 22px;
    width: 22px;
    background-color: ${progress === 'todo' ? theme.colours.curiousBlue : theme.colours.white};
    border-radius: 11px;

    ${theme.breakpoints.down('md')} {
        display: none;
    }
`;

const stageTextStyle = (progress: string) => css`
    margin: auto 0 auto 15px;
    font-size: 18px;
    z-index: ${theme.zIndex.zIndexTwo};
    color: ${progress === 'todo' ? theme.colours.curiousBlue : theme.colours.white};

    ${theme.breakpoints.down('md')} {
        margin-left: 0;
    }

    ${theme.breakpoints.down('xs')} {
        font-size: 12px;
    }
`;

const stageNumberStyle = (progress: string) => css`
    margin: auto;
    font-size: 14px;
    font-family: ${theme.fonts.frutiger};
    color: ${progress === 'todo' ? theme.colours.white : theme.colours.curiousBlue};
`;

const arrowStyle = css`
    width: 13px;
    margin-right: 10px;
    
    ${theme.breakpoints.down("md")} {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
    }

    ${theme.breakpoints.down("xs")} {
        width: 9px;
        margin-right: 6px;
    }
`;

export default WizardOption;
